// 2D room editor — optional room photo + draggable product thumbnails.
// Replaces the Three.js canvas in DivisionEditorPage.
//
// Photo stored in IDB (`syhouse:room:<divisionId>`); positions in wizard store
// (roomPlacements[divId][key]). Works without a photo (checkerboard background).
//
// Grouping (#2/#3): a DeviceCombo (espelho + its modules) is placed and dragged
// as ONE unit — keyed by combo.id, shown with the espelho's image on top and a
// badge for the number of articles. Standalone SKUs (not part of any combo)
// keep their individual pins.

import { useEffect, useRef, useState, useCallback } from 'react';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import type { SywComponent } from '../hooks/useSywData';
import { friendlyName } from '../lib/catalogModel';
import { useWizardStore, type FloorPlacement, type DeviceCombo } from '../store/wizardStore';

const ROOM_PHOTO_KEY = (divId: string) => `syhouse:room:${divId}`;
const MAX_DIM = 1600;

// Espelho/product image resolver — combos reference espelho SKUs that aren't in
// the room catalog, so resolve their image from Shopify once and cache it.
const imgCache = new Map<string, string | null>();

async function compressImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const scale = Math.min(1, MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unavailable');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.82);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function clamp01(v: number): number {
  return Math.min(0.97, Math.max(0.03, v));
}

// A placement unit on the canvas: either a grouped combo or a standalone SKU.
interface Unit {
  key: string;        // combo.id or sku — the roomPlacements key
  label: string;
  img: string | null; // resolved image (espelho image for combos)
  count: number;      // articles in a combo (>1 → show group badge)
  isCombo: boolean;
}

interface Props {
  divisionId: string;
  selectedSkus: string[];
  combos: DeviceCombo[];
  componentMap: Map<string, SywComponent>;
}

export function RoomEditor2D({ divisionId, selectedSkus, combos, componentMap }: Props) {
  const { roomPlacements, setRoomProductPlacement, removeRoomProductPlacement } =
    useWizardStore();
  const divPlacements = roomPlacements[divisionId] ?? {};

  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const planRef = useRef<HTMLDivElement>(null);
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [comboImg, setComboImg] = useState<Record<string, string | null>>({});

  // Resolve each combo's espelho image (first sku) from Shopify, cached.
  useEffect(() => {
    combos.forEach((c) => {
      const esp = c.skus[0];
      if (!esp) return;
      if (imgCache.has(esp)) {
        setComboImg((prev) => (prev[c.id] === undefined ? { ...prev, [c.id]: imgCache.get(esp) ?? null } : prev));
        return;
      }
      void fetch(`/api/catalog/search?q=${encodeURIComponent(esp)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          const rows: Array<{ sku: string; imageUrl?: string }> = j?.data ?? [];
          const url = (rows.find((p) => p.sku === esp) ?? rows[0])?.imageUrl ?? null;
          imgCache.set(esp, url);
          setComboImg((prev) => ({ ...prev, [c.id]: url }));
        })
        .catch(() => {
          imgCache.set(esp, null);
          setComboImg((prev) => ({ ...prev, [c.id]: null }));
        });
    });
  }, [combos]);

  // Load room photo from IDB whenever division changes
  useEffect(() => {
    let cancelled = false;
    void idbGet<string>(ROOM_PHOTO_KEY(divisionId)).then((v) => {
      if (!cancelled) setPhotoUrl(v ?? null);
    });
    return () => { cancelled = true; };
  }, [divisionId]);

  async function handleUpload(file: File) {
    const dataUrl = await compressImage(file);
    await idbSet(ROOM_PHOTO_KEY(divisionId), dataUrl);
    setPhotoUrl(dataUrl);
  }

  async function handleRemovePhoto() {
    await idbDel(ROOM_PHOTO_KEY(divisionId));
    setPhotoUrl(null);
  }

  function posFromPointer(e: { clientX: number; clientY: number }): FloorPlacement | null {
    const rect = planRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    };
  }

  function handlePlanPointerMove(e: React.PointerEvent) {
    if (!dragKey) return;
    const pos = posFromPointer(e);
    if (pos) setRoomProductPlacement(divisionId, dragKey, pos);
  }

  const stopDrag = useCallback(() => setDragKey(null), []);
  useEffect(() => {
    if (!dragKey) return;
    window.addEventListener('pointerup', stopDrag);
    return () => window.removeEventListener('pointerup', stopDrag);
  }, [dragKey, stopDrag]);

  // Build the placement units: one per combo + one per standalone SKU.
  const comboSkus = new Set(combos.flatMap((c) => c.skus));
  const units: Unit[] = [
    ...combos.map((c) => ({
      key: c.id,
      label: c.label,
      img: comboImg[c.id] ?? null,
      count: c.skus.length,
      isCombo: true,
    })),
    ...selectedSkus
      .filter((sku) => !comboSkus.has(sku))
      .map((sku) => {
        const comp = componentMap.get(sku);
        return { key: sku, label: comp ? friendlyName(comp) : sku, img: comp?.imageUrl ?? null, count: 1, isCombo: false };
      }),
  ];

  const placed = units.filter((u) => divPlacements[u.key]);
  const unplaced = units.filter((u) => !divPlacements[u.key]);

  function Glyph({ size }: { size: 'lg' | 'sm' }) {
    return <span className={size === 'lg' ? 'text-xl' : 'text-lg'}>🪞</span>;
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-smartify-paper">
      {/* Plan canvas */}
      <div
        ref={planRef}
        className="relative min-h-0 flex-1 overflow-hidden select-none"
        style={{
          background: photoUrl
            ? `url(${photoUrl}) center/contain no-repeat #f4f4f6`
            : 'repeating-conic-gradient(#e5e7eb 0% 25%, #f4f4f6 0% 50%) 0 0 / 24px 24px',
          touchAction: 'none',
        }}
        onPointerMove={handlePlanPointerMove}
      >
        {/* Placed units (grouped combos + standalone products) */}
        {placed.map((u) => {
          const pos = divPlacements[u.key]!;
          return (
            <div
              key={u.key}
              onPointerDown={(e) => {
                e.currentTarget.setPointerCapture(e.pointerId);
                setDragKey(u.key);
              }}
              style={{
                position: 'absolute',
                left: `${pos.x * 100}%`,
                top: `${pos.y * 100}%`,
                transform: 'translate(-50%, -50%)',
                cursor: dragKey === u.key ? 'grabbing' : 'grab',
                touchAction: 'none',
              }}
              className="flex flex-col items-center gap-0.5"
            >
              <span className="relative">
                {u.img ? (
                  <img
                    src={u.img}
                    alt=""
                    draggable={false}
                    className={[
                      'h-12 w-12 rounded-lg border-2 bg-white object-contain shadow-md',
                      u.isCombo ? 'border-smartify-brand' : 'border-white',
                    ].join(' ')}
                  />
                ) : (
                  <span
                    className={[
                      'flex h-12 w-12 items-center justify-center rounded-lg border-2 bg-white shadow-md',
                      u.isCombo ? 'border-smartify-brand' : 'border-white',
                    ].join(' ')}
                  >
                    {u.isCombo ? <Glyph size="lg" /> : <span className="text-xl">🔌</span>}
                  </span>
                )}
                {u.isCombo && u.count > 1 && (
                  <span className="absolute -right-1.5 -top-1.5 grid h-4 min-w-[1rem] place-items-center rounded-full bg-smartify-brand px-1 text-[9px] font-bold text-white shadow">
                    {u.count}
                  </span>
                )}
              </span>
              <span className="max-w-[6rem] truncate rounded bg-white/90 px-1 py-px text-[9px] font-medium leading-tight text-smartify-ink shadow">
                {u.label}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeRoomProductPlacement(divisionId, u.key);
                }}
                className="rounded bg-white/80 px-1 py-px text-[9px] text-smartify-muted hover:text-red-600 leading-tight"
              >
                ×
              </button>
            </div>
          );
        })}

        {/* Empty state prompt */}
        {!photoUrl && units.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="rounded-smartify border-2 border-dashed border-smartify-line bg-white/80 px-6 py-4 text-center text-sm text-smartify-muted hover:border-smartify-brand hover:text-smartify-brand"
            >
              📷 Fotografa a divisão para visualizar os produtos no espaço real
            </button>
          </div>
        )}

        {/* Photo controls (top-right) */}
        {photoUrl ? (
          <button
            type="button"
            onClick={() => void handleRemovePhoto()}
            className="absolute right-2 top-2 rounded-smartify border border-smartify-line bg-white/90 px-2 py-1 text-xs text-smartify-muted hover:text-red-600"
          >
            Remover foto
          </button>
        ) : units.length > 0 ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="absolute right-2 top-2 rounded-smartify border border-smartify-line bg-white/90 px-2 py-1 text-xs text-smartify-muted hover:border-smartify-brand"
          >
            📷 Foto
          </button>
        ) : null}
      </div>

      {/* Unplaced tray — drag these onto the canvas above */}
      {unplaced.length > 0 && (
        <div className="border-t border-smartify-line bg-white px-3 py-2 shrink-0">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-smartify-muted">
            Toca ou arrasta para posicionar
          </p>
          <div className="flex flex-wrap gap-2">
            {unplaced.map((u) => (
              <div
                key={u.key}
                onPointerDown={(e) => {
                  // Place at centre first so the drag is tracked immediately
                  setRoomProductPlacement(divisionId, u.key, { x: 0.5, y: 0.5 });
                  e.currentTarget.setPointerCapture(e.pointerId);
                  setDragKey(u.key);
                }}
                style={{ cursor: 'grab', touchAction: 'none', userSelect: 'none' }}
                className="flex flex-col items-center gap-0.5 rounded-smartify border border-smartify-line bg-smartify-paper p-1.5"
              >
                {u.img ? (
                  <img src={u.img} alt="" draggable={false} className="h-10 w-10 rounded object-contain" />
                ) : (
                  <span className="flex h-10 w-10 items-center justify-center rounded">
                    {u.isCombo ? <Glyph size="sm" /> : <span className="text-xl">🔌</span>}
                  </span>
                )}
                <span className="max-w-[4rem] truncate text-[9px] text-smartify-muted leading-tight text-center">
                  {u.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void handleUpload(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}
